// Plain-language step and error text for the UI. Selectors, XPath and scripts stay out of sight:
// each step reads as a sentence ("Click the schedule interview button"), and the technical form is
// kept as `detail` for a tooltip. Loaded by main.js (summarize) and by the UI (run errors).
(function (root) {
  const q = (t) => `“${t}”`;

  /** "interview-schedule-btn" → "interview schedule button" */
  function words(id) {
    return id.replace(/^#/, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(/[-_.\s]+/)
      .filter(Boolean)
      .map((w) => (/\d/.test(w) ? w : w.toLowerCase()))   // keep codes like INT001 as written
      .join(' ')
      .replace(/\bbtn\b/g, 'button')
      .replace(/\b(input|txt)$/, 'field')
      .replace(/\bdlg\b/g, 'dialog');
  }

  const NOUN = { a: 'link', button: 'button', input: 'field', select: 'dropdown', textarea: 'text box', img: 'image' };

  const isRaw = (p) => /^(\[|\/\/|\(\/\/|xpath=|#|\.)/.test(p);

  /** The best human name a CSS selector or XPath carries: visible text first, then an id-like name. */
  function fromSelector(sel) {
    const texts = [
      /text\(\)\s*=\s*['"](.+?)['"]/,
      /contains\(\s*(?:text\(\)|\.|normalize-space\([^)]*\))\s*,\s*['"](.+?)['"]\)/,
      /normalize-space\([^)]*\)\s*=\s*['"](.+?)['"]/,
      /:has-text\(\s*["'](.+?)["']\s*\)/,
      /:text(?:-is)?\(\s*["'](.+?)["']\s*\)/,
      /aria-label\s*[*^$]?=\s*['"](.+?)['"]/,
      /placeholder\s*[*^$]?=\s*['"](.+?)['"]/,
      /title\s*=\s*['"](.+?)['"]/,
    ];
    for (const re of texts) {
      const m = sel.match(re);
      if (m) return q(m[1]);
    }
    const ids = [...sel.matchAll(/(?:data-testid|data-test|data-qa|@id|\bid|@name|\bname)\s*[*^$]?=\s*['"]?([\w-]+)/g)].map((m) => m[1]);
    if (ids.length) return `the ${words(ids[ids.length - 1])}`;
    const hash = sel.match(/#([\w-]+)/);
    if (hash) return `the ${words(hash[1])}`;
    const role = sel.match(/role\s*=\s*['"]?(\w+)/);
    if (role) return `the ${role[1]}`;
    const cls = sel.match(/(?:^|[\s>+~])([a-z]+)?\.([\w-]+)[^\s>+~]*$/i);   // last compound, e.g. a.nav-contact
    if (cls) {
      const name = words(cls[2]);
      const noun = NOUN[(cls[1] || '').toLowerCase()];
      return `the ${name}${noun && !name.includes(noun) ? ` ${noun}` : ''}`;
    }
    return 'an element on the page';
  }

  /** What a step acts on, as words. */
  function target(s) {
    const p = String(s.param || '').trim();
    const t = String(s.paramType || '').toLowerCase();
    if (!p) return 'the page';
    if (!isRaw(p)) {
      switch (t) {
        case 'text': case 'linktext': case 'partiallinktext': case 'label': case 'placeholder':
          return q(p);
        case 'role': {
          const m = p.match(/^(\w+)(?:\s*\[\s*name\s*=\s*["'](.+?)["'])?/);
          if (m) return m[2] ? `the ${q(m[2])} ${m[1]}` : `the ${m[1]}`;
          break;
        }
        case 'testid': case 'id': case 'idselector': case 'name': case 'classname':
          return `the ${words(p)}`;
        case 'tagname':
          return `the ${p} element`;
      }
    }
    return fromSelector(p);
  }

  function shortUrl(u) {
    return String(u || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  /** What a page script does, from its shape, naming the element it works on when it can. */
  function script(js) {
    const s = String(js || '');
    const sel = s.match(/querySelector(?:All)?\(\s*(['"`])(.+?)\1\s*\)/);
    const what = sel && !/^(ul|li|div|span)\b/.test(sel[2]) ? fromSelector(sel[2]) : null;
    const on = what && what !== 'an element on the page' ? ` ${what}` : '';
    const option = s.match(/(?:includes|===?)\s*\(?\s*['"]([^'"]{2,40})['"]/);
    if (/role=["']?option|listbox|menuitem/.test(s)) return `Pick ${option ? q(option[1]) : 'an option'} from the list (page script)`;
    if (/value\b|ValueSetter|dispatchEvent\(new Event\(['"](input|change)/.test(s)) {
      const val = s.match(/\.set\.call\(\s*\w+\s*,\s*['"]([^'"]{1,40})['"]/) || s.match(/\.value\s*=\s*['"]([^'"]{1,40})['"]/);
      return `Set${on || ' a field'}${val ? ` to ${q(val[1])}` : ''} (page script)`;
    }
    if (/\.click\(\)/.test(s)) return `Click${on || ' an element'} (page script)`;
    if (/scroll/i.test(s)) return 'Scroll the page';
    return 'Run a page script';
  }

  /** Technical form, for the tooltip. Secrets show as dots. */
  function technical(s) {
    const shown = s.actionType === 'secret_input' ? '••••' : s.extraValue?.[0];
    return `${s.actionType}${s.param ? ` ${s.paramType ? s.paramType + '=' : ''}${s.param}` : ''}${shown ? ` "${shown}"` : ''}`;
  }

  /** { text, detail, kind } for one step; kind is "check", "milestone" or "action". */
  function describeStep(s) {
    const v = s.extraValue?.[0];
    const p = s.param;
    let text, kind = 'action';
    switch (s.actionType) {
      case 'url': text = `Open ${shortUrl(p)}`; break;
      case 'click': text = `Click ${target(s)}`; break;
      case 'clear': text = `Clear ${target(s)}`; break;
      case 'text_input': text = `Type ${q(v ?? '')} into ${target(s)}`; break;
      case 'secret_input': text = `Enter the saved password into ${target(s)}`; break;
      case 'milestone': text = p || 'Checkpoint'; kind = 'milestone'; break;
      case 'assert_visible': text = `Check ${target(s)} is shown`; kind = 'check'; break;
      case 'assert_exist': text = `Check ${target(s)} exists`; kind = 'check'; break;
      case 'assert_hidden': text = `Check ${target(s)} is not shown`; kind = 'check'; break;
      case 'assert_text': text = `Check ${target(s)} shows ${q(v ?? '')}`; kind = 'check'; break;
      case 'assert_js': text = 'Check the page is in the expected state'; kind = 'check'; break;
      case 'exec_js': text = script(p); break;
      case 'wait': {
        const ms = Number(p);
        text = Number.isFinite(ms) && ms > 0 ? `Wait ${ms >= 1000 ? `${+(ms / 1000).toFixed(1)} s` : `${ms} ms`}` : 'Wait';
        break;
      }
      default: text = technical(s);
    }
    text = text.charAt(0).toUpperCase() + text.slice(1);
    return { text, detail: technical(s), kind };
  }

  /** A run error in words. Playwright messages often embed the locator; that goes to the tooltip. */
  function describeFailure(msg) {
    const m = String(msg || '');
    if (/strict mode violation/i.test(m)) return 'More than one element matches this step';
    if (/expected to be visible|to be visible/i.test(m)) return 'The element didn\'t appear';
    if (/expected to be hidden|to be hidden/i.test(m)) return 'The element is still showing';
    if (/to have text|to contain text|expected.*text/i.test(m)) return 'The text on the page was different';
    if (/intercepts pointer events|intercepted/i.test(m)) return 'Something on the page covered the element';
    if (/not attached|detached/i.test(m)) return 'The element disappeared while being used';
    if (/net::ERR_|ERR_NAME_NOT_RESOLVED|ERR_CONNECTION/i.test(m)) return 'The page couldn\'t be reached';
    if (/Timeout \d+ms exceeded/i.test(m)) return 'Timed out waiting for the element';
    if (/assert_js|assertion failed|returned false/i.test(m)) return 'The page wasn\'t in the expected state';
    return m.replace(/locator\((.*)\)/g, 'the element').replace(/\s+/g, ' ').trim() || 'Step failed';
  }

  const api = { describeStep, describeFailure };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.wdDescribe = api;
})(this);
